import type { Ref } from 'react'
import type {
  TransactionItemViewModel,
  TransactionsTableViewModel,
} from '@/features/billing/transactions/TransactionsTable/TransactionsTableViewModel'
import { observer } from 'mobx-react-lite'
import {
  Table,
  TableBody,
  TableCell,
  TableCellSkeleton,
  TableColumn,
  TableHeader,
  TableRow,
  TableRowSkeleton,
} from '@/components/Table'
import { IconButton } from '@/components/IconButton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { moneyFormatters } from '@/utils/currency-util'
import { dateFormatters } from '@/utils/date-util'
import { CardIcon } from '@/components/CardIcon'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import {
  Skeleton,
  type SkeletonColor,
  SkeletonColorContext,
} from '@/components/Skeleton'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { links } from '@/utils/link'
import styles from './TransactionsTable.module.css'

export interface TransactionsTableProps {
  'aria-label'?: string
  'aria-labelledby'?: string
  variant?: 'default' | 'compact'
  skeletonColor?: SkeletonColor
  vm: TransactionsTableViewModel
  testId?: string
}

/**
 * The TransactionsTable
 */
export const TransactionsTable = observer(function TransactionsTable({
  vm,
  variant = 'default',
  skeletonColor,
  testId,
  ...rest
}: TransactionsTableProps) {
  const isSpacious = useIsSpacious()
  const isCompact = variant === 'compact' || !isSpacious

  return (
    <SkeletonColorContext value={skeletonColor}>
      <Table {...rest} rowSpacing="lg" data-test={testId}>
        {isCompact ? (
          <CompactTableHeader vm={vm} />
        ) : (
          <DefaultTableHeader vm={vm} />
        )}

        <TableBody>
          {vm.transactionVms.items.map((tvm) => {
            return isCompact ? (
              <CompactTableRow key={tvm.transaction.id} vm={vm} tvm={tvm} />
            ) : (
              <DefaultTableRow key={tvm.transaction.id} vm={vm} tvm={tvm} />
            )
          })}

          {isCompact ? (
            <CompactSkeleton vm={vm} />
          ) : (
            <DefaultSkeleton vm={vm} />
          )}
        </TableBody>
      </Table>
    </SkeletonColorContext>
  )
})

/**
 * Separator in a label.
 */
function LabelSeparator() {
  return <span className={styles.separator}>&nbsp;&bull;&nbsp;</span>
}

interface SkeletonProps {
  vm: TransactionsTableViewModel
}

const CompactSkeleton = observer(({ vm }: SkeletonProps) => {
  const isSpacious = useIsSpacious()

  return (
    <SkeletonListInfiniteScrollTrigger
      scroller={vm.scrollProvider}
      maxSkeletons={4}
    >
      {({ varyPercent, ref }) => (
        <TableRowSkeleton ref={ref as Ref<HTMLTableRowElement>}>
          <TableCellSkeleton>
            <p className={styles.receiptNumber}>
              <Skeleton width={varyPercent(10)} />
            </p>
            <p className={styles.displayName}>
              <Skeleton width={varyPercent(60)} />
            </p>
            <p className={styles.label}>
              <Skeleton width={varyPercent(20)} />
            </p>
          </TableCellSkeleton>
          {isSpacious && (
            <TableCellSkeleton align="right">
              <span className={styles.amountColumnText}>
                <Skeleton width={varyPercent(30)} />
                <CardIcon isSkeleton size="sm" brand="unknown" />
              </span>
            </TableCellSkeleton>
          )}

          <TableCellSkeleton isActions>
            <IconButton name="more" isSkeleton label="More options" />
          </TableCellSkeleton>
        </TableRowSkeleton>
      )}
    </SkeletonListInfiniteScrollTrigger>
  )
})

const DefaultSkeleton = observer(({ vm }: SkeletonProps) => {
  return (
    <SkeletonListInfiniteScrollTrigger
      scroller={vm.scrollProvider}
      maxSkeletons={4}
    >
      {({ vary, ref }) => (
        <TableRowSkeleton ref={ref as Ref<HTMLTableRowElement>}>
          <TableCellSkeleton>
            <Skeleton width={68} />
          </TableCellSkeleton>

          <TableCellSkeleton align="left">
            <Skeleton width={vary(250)} />
          </TableCellSkeleton>

          <TableCellSkeleton align="left">
            <span className="flex items-center">
              <CardIcon isSkeleton size="sm" brand="unknown" />
              <Skeleton width={vary(50)} />
            </span>
          </TableCellSkeleton>

          <TableCellSkeleton align="right">
            <Skeleton width={vary(30)} />
          </TableCellSkeleton>
        </TableRowSkeleton>
      )}
    </SkeletonListInfiniteScrollTrigger>
  )
})

interface TableRowProps {
  vm: TransactionsTableViewModel
  tvm: TransactionItemViewModel
}

interface TableHeaderProps {
  vm: TransactionsTableViewModel
}

const CompactTableHeader = observer(({ vm }: TableHeaderProps) => {
  const isSpacious = useIsSpacious()

  return (
    <TableHeader
      isSkeleton={
        vm.scrollProvider.fetch.pending && !vm.scrollProvider.isRevalidating
      }
    >
      <TableColumn isRowHeader>Details</TableColumn>
      {isSpacious && (
        <TableColumn align="right">
          <span className="pr-md">Amount paid</span>
        </TableColumn>
      )}
      <TableColumn isRowActionsHeader />
    </TableHeader>
  )
})

const DefaultTableHeader = observer(({ vm }: TableHeaderProps) => {
  return (
    <TableHeader
      isSkeleton={
        vm.scrollProvider.fetch.pending && !vm.scrollProvider.isRevalidating
      }
    >
      <TableColumn isRowHeader>Receipt number</TableColumn>

      <TableColumn align="left">
        <span className="pr-md">Date paid</span>
      </TableColumn>

      <TableColumn align="left">
        <span className="pr-md">Payment method</span>
      </TableColumn>

      <TableColumn align="right">
        <span>Amount paid</span>
      </TableColumn>
    </TableHeader>
  )
})

const DefaultTableRow = observer(({ vm, tvm }: TableRowProps) => {
  return (
    <TableRow key={tvm.transaction.id}>
      <TableCell>#{tvm.transaction.id}</TableCell>
      <TableCell>
        {dateFormatters.dateTimeDefault.format(tvm.transaction.dateCreated)}
      </TableCell>
      <TableCell align="right">
        <div className="flex items-center">
          {vm.fetchBillingProfileForMember.pending ? (
            <Skeleton width={20} />
          ) : (
            <>
              <CardIcon size="sm" brand={tvm.billingMethodBrand} /> -{' '}
              {tvm.billingMethodLast4}
            </>
          )}
        </div>
      </TableCell>
      <TableCell align="right">
        {moneyFormatters.default.format(tvm.signedAmount)}
      </TableCell>
    </TableRow>
  )
})

const CompactTableRow = observer(({ tvm }: TableRowProps) => {
  const isSpacious = useIsSpacious()

  return (
    <TableRow key={tvm.transaction.id}>
      <TableCell>
        <p className={styles.receiptNumber}>#{tvm.transaction.id}</p>
        <p className={styles.displayName}>{tvm.displayName}</p>
        <p className={styles.label}>
          {dateFormatters.dateMedium.format(tvm.transaction.dateCreated)}
          {!isSpacious ? (
            <>
              <LabelSeparator />
              {tvm.isRefund ? (
                <>
                  Refunded {moneyFormatters.default.format(tvm.amount)} to{' '}
                  {tvm.billingMethodBrand}
                </>
              ) : (
                <>
                  Paid {moneyFormatters.default.format(tvm.amount)} with{' '}
                  {tvm.billingMethodBrand}
                </>
              )}
            </>
          ) : (
            tvm.isRefund && (
              <>
                <LabelSeparator />
                Refund
              </>
            )
          )}
        </p>
      </TableCell>
      {isSpacious && (
        <TableCell align="right">
          <span className={styles.amountColumnText}>
            {moneyFormatters.default.format(tvm.signedAmount)}
            <CardIcon size="sm" brand={tvm.billingMethodBrand} />
          </span>
        </TableCell>
      )}

      <TableCell isActions>
        <Dropdown
          title={tvm.transaction.jobName ?? ''}
          description={`#${tvm.transaction.id}`}
          trigger={<IconButton name="more" label="More options" />}
        >
          {tvm.transaction.receiptUrl && (
            <DropdownItem
              target="_blank"
              href={tvm.transaction.receiptUrl}
              icon="file"
            >
              View receipt
            </DropdownItem>
          )}

          {tvm.transaction.jobId && (
            <DropdownItem
              href={links.jobDetails(tvm.transaction.jobId)}
              icon="taxes"
            >
              View job
            </DropdownItem>
          )}
        </Dropdown>
      </TableCell>
    </TableRow>
  )
})
